// use to handle view state of sort filter slide out internally
import { fireTag } from '@vue-util/tagManager';
import {
  ref, computed,
} from 'vue';
import CacheManager from '@vue-util/cacheManager';
import useFireTagAsync from '@modules/discovery/composables/useFireTagAsync';
import { brand } from 'config';
import useFacets from './useFacets';
import { isNotDeliveryAndPickupFacet } from '../utils/deliveryAndPickupUtil';

const initialState = Object.freeze({
  accordionActiveFacetDefault: '',
  sortBySelectedValue: '',
  facets: [],
  selectionOrder: 0,
});

const accordionActiveFacet = ref(initialState.accordionActiveFacet);

const selections = ref({ ...initialState });

const allFiltersButtonClicked = ref(false);
const setAllFiltersButtonClicked = (value) => {
  allFiltersButtonClicked.value = value;
};

let globalOptions = null;
let rightSideDrawer = null;

function getSelectionValueList() {
  return selections.value.facets.filter((v) => v.alwaysDropdown !== true);
}

const setUtagFacetDataOnClose = (index) => {
  const selectionValuesList = getSelectionValueList();
  const utagFacetName = selectionValuesList[index]?.displayName;
  const enableAsyncAnalyticsTags = globalOptions?.consumerStore?.state?.envProps?.enableAsyncAnalyticsTags;
  /* istanbul ignore else */
  if (brand === 'bcom') {
    if (enableAsyncAnalyticsTags) {
      useFireTagAsync(() => fireTag('link', {
        event_name: 'facet collapse',
        facet_names: [`${utagFacetName}`],
      }));
    } else {
      fireTag('link', {
        event_name: 'facet collapse',
        facet_names: [`${utagFacetName}`],
      });
    }
  }
};

function getSelectedFacets(selection) {
  const options = [...selection.value?.facets.filter((facet) => facet?.selectedValues?.length)];
  return options;
}

const buildSections = (view) => {
  selections.value.sortBySelectedValue = view.sortBySelectedValue;
  selections.value.facets = JSON.parse(JSON.stringify(view.facets));

  // facet id were created based on name. See useFacetOptions.js
  accordionActiveFacet.value = (view.facetId === 'filter')
    ? initialState.accordionActiveFacetDefault
    : view.facetId;
};

const facetByName = (name, parent) => {
  /* istanbul ignore else */
  if (parent) {
    const result = selections.value.facets.find((facet) => facet.name === parent);
    return result.children.find((child) => child.displayName === name);
  }
  return selections.value.facets.find((facet) => facet.name === name);
};

const updateSortBy = (value) => {
  selections.value.sortBySelectedValue = value;
};

function onSelectedOption(selection, { isClearAll }) {
  const selectedFacets = getSelectedFacets(selection);
  globalOptions.onSelectedOption(selectedFacets, isClearAll);
}

const updateFacet = (criteria) => {
  // is this same as facetByName?
  const facet = facetByName(criteria.name, criteria.parent);
  const maxOrderNumber = 10000;
  /* istanbul ignore else */
  if (facet) {
    const selectedFacets = getSelectedFacets(selections);
    const minOrder = selectedFacets.reduce((min, f) => (
      ((f.selectionOrder === undefined) ? maxOrderNumber : f.selectionOrder) < min ? f.selectionOrder : min
    ), maxOrderNumber);
    facet.selectedValues = criteria.selectedValues;
    facet.selectionOrder = criteria.selectedValues.length ? minOrder - 1 : maxOrderNumber;
    /* istanbul ignore else */
    if (criteria?.parent) {
      const parent = facetByName(criteria.parent);
      parent.selectionOrder = facet.selectionOrder;
    }
  }
  onSelectedOption(selections, { isClearAll: false });
};

const addCheckboxValue = (criteria) => {
  // is this same as facetByName?
  const facet = facetByName(criteria.name);
  if (facet) {
    facet.values.push(criteria.newValue);
  }
};

const removeCheckBoxValuesByValue = (criteria) => {
  const facet = selections.value.facets.filter((_facet) => _facet.name === criteria.name);
  /* istanbul ignore else */
  if (facet.length > 0) {
    facet[0].values = facet[0].values.filter((value) => value.value !== criteria.value);
  }
};

const resetSelections = () => {
  globalOptions.slideoutFacets.value = null;
  selections.value = { ...initialState };
  accordionActiveFacet.value = initialState.accordionActiveFacetDefault;
  // remove size session
  CacheManager.removeSession('selectedSizeOption');
  globalOptions.mySizesData.value.toggleValue = globalOptions.mySizesData.value.toggleAppearance;
};

const setGlobalOptions = (options) => {
  /* istanbul ignore else */
  if (!globalOptions) {
    globalOptions = options;
  }
};

export function useSortFilterSelections() {
  const setSortFilterSelections = (facets = []) => {
    selections.value.facets = facets;
  };

  return {
    setSortFilterSelections,
  };
}

export default function useSortFilter() {
  const {
    globalOptions: globalOptionsOriginal,
    slideoutProductCount,
    slideoutFacetSelectedCount,
  } = useFacets();

  setGlobalOptions(globalOptionsOriginal);

  const isSYS = globalOptions?.isSYS;

  const accordionAllFilterButton = computed(() => allFiltersButtonClicked.value);
  rightSideDrawer = computed(() => accordionAllFilterButton.value);

  const setAccordionActiveFacetIndex = (index) => {
    let selectionValuesList = getSelectionValueList();
    // dnf isn't a part of accordions list
    /* istanbul ignore else */
    if (isSYS.value) {
      selectionValuesList = selectionValuesList.filter(isNotDeliveryAndPickupFacet);
    }
    const utagFacetName = selectionValuesList[index]?.displayName;
    accordionActiveFacet.value = `facet_${selectionValuesList[index]?.displayName}`;
    const enableAsyncAnalyticsTags = globalOptions?.consumerStore?.state?.envProps?.enableAsyncAnalyticsTags;

    /* istanbul ignore else */
    if (brand === 'bcom') {
      if (enableAsyncAnalyticsTags) {
        useFireTagAsync(() => fireTag('link', {
          event_name: 'facet expand',
          facet_names: [`${utagFacetName}`],
          facet_values: ['no facet'],
        }));
      } else {
        fireTag('link', {
          event_name: 'facet expand',
          facet_names: [`${utagFacetName}`],
          facet_values: ['no facet'],
        });
      }
    }
  };

  const clearAllSelectedFacets = (getFacetTypeUtil, singleClearedFacet = {}) => {
    selections.value.facets
      .filter((facet) => facet.selectedValues.length > 0 && (rightSideDrawer.value || facet.displayName === singleClearedFacet.displayName))
      .forEach((facet) => {
        const isCustom = getFacetTypeUtil && getFacetTypeUtil(facet).isCustomOption;
        if (isSYS.value) {
          /* istanbul ignore else */
          if (facet.facetType !== 'DELIVERY_AND_PICKUP') {
            facet.selectedValues = [];
          }
        } else {
          facet.selectedValues = [];
        }
        /* istanbul ignore else */
        if (isCustom) {
          facet.values = facet.values.filter((v) => !isCustom(v));
        }
      });
    selections.value.facets
      .filter((facet) => facet.children && (rightSideDrawer.value || facet.name === singleClearedFacet.parent))
      .forEach((facet) => {
        facet.children.forEach((child) => {
          facet.selectedValues = [];
          child.selectedValues = [];
        });
      });
    onSelectedOption(selections, { isClearAll: true });
    /* istanbul ignore else */
    if (brand === 'bcom') {
      globalOptions.mySizesData.value.toggleValue = false;
    }
  };

  const accordionActiveIndex = computed(() => {
    let found = false;
    let activeIdx = -1;
    let hiddenCount = 0;
    let { facets } = selections.value;
    /* istanbul ignore else */
    if (isSYS.value && rightSideDrawer.value) {
      // D&F isn't presented in facets accordions list
      facets = facets.filter(isNotDeliveryAndPickupFacet);
    }

    for (let i = 0; i < facets.length && !found; i++) {
      /* istanbul ignore else */
      if (facets[i].alwaysDropdown) {
        hiddenCount++;
      }
      /* istanbul ignore else */
      if (`facet_${facets[i].displayName}` === accordionActiveFacet.value) {
        activeIdx = i;
        found = true;
      }
    }
    /* istanbul ignore else */
    if (activeIdx > -1) {
      activeIdx -= hiddenCount;
    }
    return activeIdx;
  });

  const allSingleSelectedFacets = computed(() => selections.value.facets[accordionActiveIndex.value]);

  return {
    addCheckboxValue,
    removeCheckBoxValuesByValue,
    resetSelections,
    clearAllSelectedFacets,
    updateSortBy,
    updateFacet,
    facetByName,
    buildSections,
    onSelectedOption,
    selections,
    slideoutProductCount,
    slideoutFacetSelectedCount,
    accordionActiveIndex,
    setAccordionActiveFacetIndex,
    setUtagFacetDataOnClose,
    allSingleSelectedFacets,
    rightSideDrawer,
    allFiltersButtonClicked,
    setAllFiltersButtonClicked,
  };
}
